/**
 * ProductEntries — 首页「技术产品」并列模块（工单：新增两个正式产品入口）
 * 复用 xin.xyz 现有视觉系统：NeoPixelCard / neo-chip / neo-led / font-pixel /
 * contact-* 配色 / px-btn 语汇 / 8-bit 音效反馈。不引入新卡片风格，不改 Hero。
 * 轻量技术动画仅 hover/focus 触发，不抢主视觉。
 */
import { ArrowRight } from "lucide-react";
import NeoPixelCard from "@/components/NeoPixelCard";
import { audioEngine } from "@/lib/audioEngine";

/** 入口 1：RPC Manager 官网（站内 /rpc/） */
export const RPC_MANAGER_URL = "/rpc/";
/** 入口 2：DevOmni 开发者控制面（站外） */
export const DEVOMNI_URL = "https://devomni.icloser.xyz/";

interface ProductLinkProps {
  href: string;
  accent: "cyan" | "violet";
  ariaLabel: string;
  title: string;
  tagline: string;
  chips: readonly string[];
}

function ProductLink({
  href,
  accent,
  ariaLabel,
  title,
  tagline,
  chips,
}: ProductLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className={`product-entry${
        accent === "violet" ? " product-entry-violet" : ""
      }`}
      onMouseEnter={() => audioEngine.play("beep")}
      onClick={() => audioEngine.play("select")}
    >
      <NeoPixelCard accent={accent} className="relative h-full p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="neo-led neo-led-blink" aria-hidden />
            <span className="font-pixel text-[12px] tracking-[0.18em] text-[#e8f4ff]">
              {title}
            </span>
          </div>
          <span className="product-cta">
            <span>EXPLORE</span>
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
          </span>
        </div>

        <p className="mt-2 font-mono text-[11px] leading-relaxed text-[#c8e0ff]">
          {tagline}
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c} className="neo-chip">
              {c}
            </span>
          ))}
        </div>
      </NeoPixelCard>
    </a>
  );
}

export function ProductEntries() {
  return (
    <section id="products" aria-label="Technical products" className="mt-10">
      <h2 className="mb-3 font-pixel text-[9px] tracking-[0.25em] text-[#00f0ff]/80">
        TECHNICAL PRODUCTS // 技术产品
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <ProductLink
          href={RPC_MANAGER_URL}
          accent="cyan"
          ariaLabel="RPC MANAGER — portable multi-chain node control plane (opens in a new tab)"
          title="RPC MANAGER"
          tagline="Portable multi-chain node control plane."
          chips={["BSC", "Ethereum", "Polygon Wallet Node"]}
        />
        <ProductLink
          href={DEVOMNI_URL}
          accent="violet"
          ariaLabel="DEVOMNI — developer control plane for AI-assisted engineering workflows (opens in a new tab)"
          title="DEVOMNI"
          tagline="Developer control plane for AI-assisted engineering workflows."
          chips={["Agents", "Runtime", "Automation", "Tooling"]}
        />
      </div>
    </section>
  );
}

export default ProductEntries;
